/* ==========================================================================
   organischer-dünger.de – Interaktionen für alle od-* Sections
   - Warenkorb: nutzt Horizons Cart-Events, damit Cart-Drawer + Zähler mitziehen
   - Produktseite: Variantenwechsel, Galerie, Menge, Sticky-Kaufleiste
   - Saisonkalender: markiert die aktuelle Saison
   - Wetter-Check: lädt Wetterdaten erst nach Klick (Open-Meteo, keine Cookies)
   ========================================================================== */

let CartLinesUpdateEvent = null;
try {
  ({ CartLinesUpdateEvent } = await import('@shopify/events'));
} catch (e) {
  /* Ohne Horizon-Events funktioniert der Warenkorb trotzdem (Fallback: Weiterleitung) */
}

const routes = () => window.Theme?.routes || { cart_add_url: '/cart/add', cart_url: '/cart' };

function toast(msg) {
  let el = document.querySelector('.od-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'od-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.classList.add('is-visible');
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('is-visible'), 3200);
}

/**
 * Legt eine Variante in den Warenkorb und informiert Horizon (Drawer öffnet, Zähler aktualisiert).
 * @param {{id: string|number, quantity?: number, sellingPlan?: string|null, source?: Element}} opts
 */
export async function odAddToCart({ id, quantity = 1, sellingPlan = null, source = document }) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const body = { items: [{ id: Number(id), quantity: Number(quantity) }] };
  if (sellingPlan) body.items[0].selling_plan = Number(sellingPlan);
  if (sectionIds.length) body.sections = sectionIds.join(',');

  let deferred = null;
  if (CartLinesUpdateEvent) {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: Number(quantity) }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const res = await fetch(`${routes().cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Fehler');

    const cart = await fetch(`${routes().cart_url}.js`).then((r) => r.json());
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'od-add-to-cart',
          sourceId: 'od',
          itemCount: Number(quantity),
          sections: data.sections,
          didError: false,
        },
      });
    }
    if (!document.querySelector('cart-drawer-component, cart-items-component')) {
      window.location.href = routes().cart_url;
      return true;
    }
    toast('Im Warenkorb ✓');
    return true;
  } catch (err) {
    if (deferred) deferred.reject(err);
    toast(err.message || 'Das hat nicht geklappt – bitte erneut versuchen.');
    return false;
  }
}

/* ---------- Header-Höhe für „Header liegt auf dem Hero" ---------- */
const setHeaderH = () => {
  const h = document.querySelector('.header-section');
  if (h) document.documentElement.style.setProperty('--od-header-h', `${Math.round(h.getBoundingClientRect().height)}px`);
};
setHeaderH();
window.addEventListener('resize', setHeaderH, { passive: true });
if ('ResizeObserver' in window) {
  const hs = document.querySelector('.header-section');
  if (hs) new ResizeObserver(setHeaderH).observe(hs);
}

/* ---------- USP-Leisten mobil als Laufband ---------- */
if (window.matchMedia('(max-width: 749px)').matches) {
  document.querySelectorAll('.od-usp__list').forEach((list) => {
    if (list.dataset.cloned || list.children.length < 2) return;
    [...list.children].forEach((li) => {
      const c = li.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      list.appendChild(c);
    });
    list.dataset.cloned = '1';
    list.style.setProperty('--od-marquee-dur', `${list.children.length * 3.5}s`);
    list.classList.add('is-marquee');
  });
}

/* ---------- Quick-Add auf Karten ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-od-add]');
  if (!btn) return;
  e.preventDefault();
  btn.setAttribute('aria-disabled', 'true');
  await odAddToCart({ id: btn.dataset.odAdd, source: btn });
  btn.removeAttribute('aria-disabled');
});

/* ---------- Produktseite ---------- */
class OdProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[data-od-variants]');
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.form = this.querySelector('form[data-od-form]');
    this.idInput = this.querySelector('input[name="id"]');
    this.qtyInput = this.querySelector('[data-od-qty]');
    this.track = this.querySelector('[data-od-track]');

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-od-variant]')) this.selectVariant(e.target.value);
      if (e.target.matches('[name="selling_plan_choice"]')) this.updatePlan();
    });
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-od-step]');
      if (step && this.qtyInput) {
        const n = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + Number(step.dataset.odStep));
        this.qtyInput.value = n;
        this.querySelectorAll('[data-od-qty-mirror]').forEach((el) => (el.textContent = n));
      }
      const thumb = e.target.closest('[data-od-thumb]');
      if (thumb) this.goTo(Number(thumb.dataset.odThumb));
      const sticky = e.target.closest('[data-od-sticky-submit]');
      if (sticky) this.form?.requestSubmit();
    });

    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = this.form.querySelector('[type="submit"]');
      btn?.setAttribute('aria-disabled', 'true');
      const plan = this.querySelector('[name="selling_plan_choice"]:checked')?.value || null;
      await odAddToCart({
        id: this.idInput.value,
        quantity: this.qtyInput?.value || 1,
        sellingPlan: plan && plan !== 'once' ? plan : null,
        source: this,
      });
      btn?.removeAttribute('aria-disabled');
    });

    this.track?.addEventListener(
      'scroll',
      () => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.markThumb(i);
      },
      { passive: true }
    );

    this.initSticky();
  }

  goTo(i) {
    if (!this.track) return;
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' });
    this.markThumb(i);
  }

  markThumb(i) {
    this.querySelectorAll('[data-od-thumb]').forEach((t) =>
      t.classList.toggle('is-active', Number(t.dataset.odThumb) === i)
    );
  }

  selectVariant(id) {
    const v = this.variants.find((x) => String(x.id) === String(id));
    if (!v) return;
    this.idInput.value = v.id;
    const set = (sel, html) => this.querySelectorAll(sel).forEach((el) => (el.innerHTML = html ?? ''));
    set('[data-od-price]', v.price);
    set('[data-od-compare]', v.compare || '');
    set('[data-od-unit]', v.unit || '');
    set('[data-od-sticky-price]', v.price);
    this.querySelectorAll('[data-od-price]').forEach((el) => el.classList.toggle('is-sale', !!v.compare));
    this.querySelectorAll('[data-od-compare-wrap]').forEach((el) => (el.hidden = !v.compare));
    this.querySelectorAll('[data-od-sale-badge]').forEach((el) => {
      el.hidden = !v.sale_pct;
      el.textContent = v.sale_pct ? `−${v.sale_pct} %` : '';
    });
    this.querySelectorAll('[data-od-area]').forEach((el) => {
      el.hidden = !v.area;
      const n = el.querySelector('[data-od-area-num]');
      if (n) n.textContent = v.area || '';
    });
    this.querySelectorAll('[data-od-stock]').forEach((el) => {
      el.dataset.state = v.available ? 'in' : 'out';
      const txt = el.querySelector('[data-od-stock-text]');
      if (txt) txt.textContent = v.available ? el.dataset.inText : el.dataset.outText;
    });
    this.querySelectorAll('[data-od-submit]').forEach((b) => {
      b.disabled = !v.available;
      const label = b.querySelector('[data-od-submit-label]');
      if (label) label.textContent = v.available ? b.dataset.labelIn : b.dataset.labelOut;
    });
    if (v.media_index != null && v.media_index >= 0) this.goTo(v.media_index);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url);
  }

  updatePlan() {
    const checked = this.querySelector('[name="selling_plan_choice"]:checked');
    this.querySelectorAll('[data-od-plan-option]').forEach((el) =>
      el.classList.toggle('is-active', el.contains(checked))
    );
  }

  initSticky() {
    const bar = this.querySelector('[data-od-sticky]');
    const anchor = this.querySelector('[data-od-submit]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }
}
if (!customElements.get('od-product')) customElements.define('od-product', OdProduct);

/* ---------- Saisonkalender ---------- */
class OdSeasons extends HTMLElement {
  connectedCallback() {
    const m = new Date().getMonth() + 1;
    let hit = null;
    this.querySelectorAll('[data-months]').forEach((el) => {
      const months = el.dataset.months.split(',').map((x) => parseInt(x, 10));
      if (months.includes(m) && !hit) hit = el;
    });
    if (hit) {
      hit.classList.add('is-now');
      const flag = hit.querySelector('[data-now-flag]');
      if (flag) flag.hidden = false;
    }
  }
}
if (!customElements.get('od-seasons')) customElements.define('od-seasons', OdSeasons);

/* ---------- Wetter-Check (opt-in) ---------- */
class OdWeather extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.out = this.querySelector('[data-od-weather-out]');
    this.recBox = this.querySelector('[data-od-weather-rec]');
    this.recHtml = this.querySelector('template[data-od-rec-default]')?.innerHTML || '';
    this.plantHtml = '';
    document.addEventListener('od:plant', (e) => {
      this.plantHtml = e.detail?.html || '';
      if (!this.recBox.hidden) this.showRec();
    });
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const place = new FormData(this.form).get('place')?.toString().trim();
      if (place) this.load(place);
    });
  }

  async geocode(place) {
    const q = place.trim();
    // 1) Postleitzahl → Zippopotam
    if (/^\d{5}$/.test(q)) {
      const z = await fetch(`https://api.zippopotam.us/de/${q}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      const p = z?.places?.[0];
      if (p) return { name: `${p['place name']} (${q})`, latitude: Number(p.latitude), longitude: Number(p.longitude) };
    }
    // 2) Ortsname → Open-Meteo (Städte/Ortsteile)
    const om = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=1&language=de&countryCode=DE`
    ).then((r) => r.json()).catch(() => null);
    if (om?.results?.[0]) return om.results[0];
    // 3) Gemeinden & Co. → OpenStreetMap (Nominatim)
    const nm = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&countrycodes=de&limit=1&accept-language=de&q=${encodeURIComponent(q)}`
    ).then((r) => r.json()).catch(() => null);
    if (nm?.[0]) return { name: nm[0].name || q, latitude: Number(nm[0].lat), longitude: Number(nm[0].lon) };
    return null;
  }

  async load(place) {
    this.out.hidden = false;
    this.out.innerHTML = '<p class="od-weather__msg">Lade Wetterdaten …</p>';
    try {
      const loc = await this.geocode(place);
      if (!loc) throw new Error('Ort nicht gefunden – bitte Postleitzahl eingeben (z. B. 30900).');
      const wx = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=Europe%2FBerlin&forecast_days=7`
      ).then((r) => r.json());
      this.render(loc, wx.daily);
      this.showRec();
    } catch (err) {
      this.out.innerHTML = `<p class="od-weather__msg">${err.message || 'Wetterdaten gerade nicht verfügbar.'}</p>`;
    }
  }

  showRec() {
    const html = this.plantHtml || this.recHtml;
    if (!html || !this.recBox) return;
    this.recBox.innerHTML = `<p class="od-weather__rec-label">${this.dataset.recTitle || 'Das brauchst du jetzt'}</p>${html}`;
    this.recBox.hidden = false;
  }

  render(loc, d) {
    const avg = (a) => a.reduce((s, x) => s + x, 0) / a.length;
    const tMax = avg(d.temperature_2m_max);
    const tMin = Math.min(...d.temperature_2m_min);
    const rain = d.precipitation_sum.reduce((s, x) => s + x, 0);
    const rainDays = d.precipitation_sum.filter((x) => x >= 1).length;
    const t = (k) => this.dataset[k] || '';

    let verdict, cls;
    if (tMin < 0) {
      verdict = t('msgFrost');
      cls = 'bad';
    } else if (tMax >= 8 && tMax <= 26 && rainDays >= 1 && rain <= 40) {
      verdict = t('msgGood');
      cls = 'good';
    } else if (tMax > 26 && rain < 2) {
      verdict = t('msgHot');
      cls = 'mid';
    } else if (rain < 1) {
      verdict = t('msgDry');
      cls = 'mid';
    } else {
      verdict = t('msgOk');
      cls = 'mid';
    }

    const days = d.time
      .map((iso, i) => {
        const day = new Date(iso).toLocaleDateString('de-DE', { weekday: 'short' });
        const r = d.precipitation_sum[i];
        return `<li><span>${day}</span><b>${Math.round(d.temperature_2m_max[i])}°</b><small>${r >= 0.5 ? r.toFixed(1) + ' l' : '–'}</small></li>`;
      })
      .join('');

    this.out.innerHTML = `
      <div class="od-weather__result od-weather__result--${cls}">
        <p class="od-weather__place">7 Tage in ${loc.name}</p>
        <div class="od-weather__kpis">
          <span><b>${Math.round(tMax)} °C</b> Ø Höchstwert</span>
          <span><b>${rain.toFixed(1)} l/m²</b> Regen</span>
        </div>
        <p class="od-weather__verdict">${verdict}</p>
        <ul class="od-weather__days">${days}</ul>
        <p class="od-weather__src">Daten: Open-Meteo.com</p>
      </div>`;
  }
}
if (!customElements.get('od-weather')) customElements.define('od-weather', OdWeather);

/* ---------- Dünge-Finder ---------- */
class OdFinder extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-od-tab]')];
    this.tabs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.odTab)));
    this.addEventListener('keydown', (e) => {
      if (!e.target.matches('[data-od-tab]') || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
      const i = this.tabs.indexOf(e.target);
      const n = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
      n.focus();
      this.select(n.dataset.odTab);
    });
    const m = new Date().getMonth() + 1;
    this.querySelectorAll(`[data-month="${m}"]`).forEach((li) => li.classList.add('is-current'));
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-od-to-weather]');
      if (!btn) return;
      this.announce();
      const target = document.querySelector(btn.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => target.querySelector('input')?.focus({ preventScroll: true }), 500);
      }
    });
  }

  select(id) {
    this.tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.odTab === id)));
    this.querySelectorAll('[data-od-panel]').forEach((p) => (p.hidden = p.dataset.odPanel !== id));
    this.current = id;
    this.announce();
  }

  announce() {
    const id = this.current || this.tabs[0]?.dataset.odTab;
    const tpl = this.querySelector(`[data-od-panel="${id}"] template[data-od-rec-template]`);
    if (tpl) document.dispatchEvent(new CustomEvent('od:plant', { detail: { html: tpl.innerHTML } }));
  }
}
if (!customElements.get('od-finder')) customElements.define('od-finder', OdFinder);
